import { useCallback, useRef, useState } from 'react'

import { clamp } from 'lodash'

const DEFAULT_SATURATION_PX = 10

type Orientation = 'horizontal' | 'vertical'

const measureFade = (
  el: HTMLElement,
  saturationPx: number,
  orientation: Orientation,
) => {
  const [scroll, scrollSize, clientSize] =
    orientation === 'vertical'
      ? [el.scrollTop, el.scrollHeight, el.clientHeight]
      : [el.scrollLeft, el.scrollWidth, el.clientWidth]
  return {
    start: clamp(scroll / saturationPx, 0, 1),
    end: clamp((scrollSize - clientSize - scroll) / saturationPx, 0, 1),
  }
}

/**
 * Tracks scroll position on one axis and returns start/end edge-fade
 * opacities — left/right for `horizontal`, top/bottom for `vertical`. Spread
 * `containerProps` onto the scrollable element; `containerRef` exposes the
 * same node for imperative reads (e.g. `querySelector`).
 */
export const useScrollEdgeFade = ({
  orientation = 'horizontal',
  saturationPx = DEFAULT_SATURATION_PX,
}: { orientation?: Orientation; saturationPx?: number } = {}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [fade, setFade] = useState({ start: 0, end: 0 })

  const remeasure = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const next = measureFade(el, saturationPx, orientation)
    setFade((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next,
    )
  }, [saturationPx, orientation])

  // React 19: returning a cleanup from a callback ref handles teardown,
  // so we don't need a useEffect for the ResizeObserver lifecycle.
  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el
      if (!el) return
      remeasure()
      el.addEventListener('scroll', remeasure, { passive: true })
      const observer = new ResizeObserver(remeasure)
      observer.observe(el)
      return () => {
        el.removeEventListener('scroll', remeasure)
        observer.disconnect()
      }
    },
    [remeasure],
  )

  return {
    containerRef,
    containerProps: { ref: setContainerRef },
    startOpacity: fade.start,
    endOpacity: fade.end,
    remeasure,
  }
}
